import type { JSX } from 'react'

import { NoteBlankIcon } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  NotesPopover,
  NotesPopoverActions,
  NotesPopoverExpandButton,
  NotesPopoverHeader,
  NotesPopoverTitle,
  useNotesPopoverRootRef,
} from '~publish/components/Notes'
import type { NotesPopoverTriggerProps } from '~publish/components/Notes'
import {
  DRAFT_NOTES_VISIBILITY,
  NotesVisibilityHint,
} from '~publish/components/PostNotes/NotesVisibilityHint'
import { SplitTagBadge } from '~publish/components/SplitTagBadge/SplitTagBadge'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { CONTENT_ITEM_NOTES_SPLIT } from '~publish/hooks/notes/useContentItemNotesEnabled'

import { DraftNotesThread } from './DraftNotesThread'
import { useContentItemNotesThread } from './useContentItemNotesThread'

/**
 * The Notes icon on a draft card, opening the draft's own thread in a
 * popover, as queue cards do for a Post. Expand closes the popover and runs
 * `onExpand`, which opens the draft's full Notes view; it is left out when
 * the host has none to offer.
 *
 * The trigger reads nothing: the thread's summary loads only while the
 * popover is open, so a list of drafts makes no request per card. It shows no
 * unread dot, because ContentItem Notes do not track read state yet. The
 * host decides whether to offer Notes, with `useContentItemNotesEnabled`.
 */
export function DraftNotesEntryPoint({
  contentItemId,
  text,
  onExpand,
  variant,
  size,
  className,
}: NotesPopoverTriggerProps & {
  contentItemId: string
  /** The draft's text, for the "Notes viewed" event. */
  text: string
  onExpand?: () => void
}): JSX.Element {
  const organizationId = useOrganizationId()
  const trackOpen = (): void => {
    if (!organizationId) return
    BufferTracker.noteFeedViewed({
      entityId: contentItemId,
      entityType: 'contentItem',
      channel: null,
      channelId: null,
      channelType: null,
      channelUsername: null,
      clientName: 'publishWeb',
      entityStatus: 'draft',
      entityText: text,
      organizationId,
    })
  }

  return (
    <NotesPopover
      variant={variant}
      size={size}
      className={className}
      label="Notes"
      tooltip="Notes"
      icon={<NoteBlankIcon />}
      onOpen={trackOpen}
      data-testid="draft-notes-popover"
    >
      <DraftNotesPopoverThread
        contentItemId={contentItemId}
        onExpand={onExpand}
      />
    </NotesPopover>
  )
}

function DraftNotesPopoverThread({
  contentItemId,
  onExpand,
}: {
  contentItemId: string
  onExpand?: () => void
}): JSX.Element {
  const { thread, markClosed } = useContentItemNotesThread(contentItemId)
  const rootRef = useNotesPopoverRootRef()

  return (
    <DraftNotesThread
      contentItemId={contentItemId}
      thread={thread}
      markClosed={markClosed}
      focusComposer
      header={
        <NotesPopoverHeader>
          <NotesPopoverTitle>
            <SplitTagBadge splitName={CONTENT_ITEM_NOTES_SPLIT} />
            <NotesVisibilityHint tooltip={DRAFT_NOTES_VISIBILITY} />
          </NotesPopoverTitle>
          {onExpand && (
            <NotesPopoverActions>
              <NotesPopoverExpandButton onClick={onExpand} />
            </NotesPopoverActions>
          )}
        </NotesPopoverHeader>
      }
      fallbackFocusRef={rootRef}
    />
  )
}
